import React, {useState, createContext, PropsWithChildren} from 'react';
import {LabelMap} from '../../store/dataService';
import {DatasetMetadataLabels} from '../../types/dataService/DatasetMetadata';
import {DatasetBinary} from './DatasetLoader';

type StudioDatasetViewerDataType = (DatasetBinary & {hasVideoAndImage?: boolean}) | null;

type StudioDatasetViewerStatus = 'LOADING' | 'IDLE';
interface StudioDatasetViewerContextInterface {
  selections: string[];
  setSelections: (val: string[]) => void;

  binaries: StudioDatasetViewerDataType;
  setBinaries: (val: StudioDatasetViewerDataType) => void;
  allBinaries: StudioDatasetViewerDataType;
  setAllBinaries: (val: StudioDatasetViewerDataType) => void;
  labels?: DatasetMetadataLabels;
  setLabels: (val?: DatasetMetadataLabels) => void;
  labelMap: LabelMap;
  setLabelMap: (val?: LabelMap) => void;
  status: StudioDatasetViewerStatus;
  setStatus: (val?: StudioDatasetViewerStatus) => void;

  refreshBinaries: () => void;
  refreshLabels: () => void;
  refreshCountBinary: number;
  refreshCountLabel: number;

  selectFirst: () => void;
  selectNext: () => void;
  selectPrevious: () => void;
  selectById: (id: string) => void;
}

const initState = {
  selections: [],
  setSelections: (val: string[]) => {},
  binaries: null,
  setBinaries: (val: StudioDatasetViewerDataType) => {},
  allBinaries: null,
  setAllBinaries: (val: StudioDatasetViewerDataType) => {},
  labels: undefined,
  setLabels: (val?: DatasetMetadataLabels) => {},
  labelMap: {},
  setLabelMap: (val?: LabelMap) => {},
  status: 'IDLE' as StudioDatasetViewerStatus,
  setStatus: (val?: StudioDatasetViewerStatus) => {},

  refreshBinaries: () => {},
  refreshLabels: () => {},
  refreshCountBinary: 0,
  refreshCountLabel: 0,

  selectFirst: () => {},
  selectNext: () => {},
  selectPrevious: () => {},
  selectById: (id: string) => {},
};

export const StudioDatasetViewerContext = createContext<
  StudioDatasetViewerContextInterface
>(initState);

export const StudioDatasetViewerConsumer = StudioDatasetViewerContext.Consumer;

export const StudioDatasetViewerProvider = (props: PropsWithChildren<{}>) => {
  const [selections, setSelections] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [labelMap, setLabelMap] = useState<LabelMap>({});
  const [status, setStatus] = useState<StudioDatasetViewerStatus>('IDLE');

  const [binaries, setBinaries] = useState<StudioDatasetViewerDataType>(null);
  const [allBinaries, setAllBinaries] = useState<StudioDatasetViewerDataType>(null);
  const [labels, setLabels] = useState<DatasetMetadataLabels | undefined>();

  const [refreshCountBinary, setRefreshCountBinary] = useState<number>(0);
  const [refreshCountLabel, setRefreshCountLabel] = useState<number>(0);

  const refreshBinaries = () => setRefreshCountBinary(refreshCountBinary + 1);
  const refreshLabels = () => setRefreshCountLabel(refreshCountLabel + 1);

  const selectFirst = () => {
    setSelectedIndex(0);
    setSelections(binaries ? [binaries?.list[0].id] : []);
  };

  const selectNext = () => {
    const nextIndex = selectedIndex + 1;
    if (binaries && nextIndex < binaries.list.length) {
      setSelectedIndex(nextIndex);
      setSelections([binaries?.list[nextIndex].id]);
    }
  };

  const selectPrevious = () => {
    const previousIndex = selectedIndex - 1;
    if (binaries && previousIndex >= 0) {
      setSelectedIndex(previousIndex);
      setSelections([binaries?.list[previousIndex].id]);
    }
  };

  const selectById = (id: string) => {
    if (binaries) {
      const index = binaries.list.findIndex(el => el.id === id);
      if (index > -1) {
        setSelectedIndex(index);
        setSelections([binaries?.list[index].id]);
      }
    }
  };

  return (
    <StudioDatasetViewerContext.Provider
      value={
        {
          selections,
          setSelections,
          binaries,
          setBinaries,
          allBinaries,
          setAllBinaries,
          labels,
          setLabels,
          labelMap,
          setLabelMap,
          status,
          setStatus,

          refreshBinaries,
          refreshLabels,

          refreshCountBinary,
          refreshCountLabel,

          selectFirst,
          selectNext,
          selectPrevious,
          selectById,
        } as StudioDatasetViewerContextInterface
      }
    >
      {props.children}
    </StudioDatasetViewerContext.Provider>
  );
};

export function useStudioDatasetViewerContext() {
  const context = React.useContext<StudioDatasetViewerContextInterface>(
    StudioDatasetViewerContext
  );
  if (context === undefined) {
    throw new Error(
      '!!useStudioDatasetViewerContext must be used within a StudioDatasetViewerProvider'
    );
  }
  return context;
}
